import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { RecurringState } from '../money.model';
import { detectRecurring, RecurringSeries } from '../rules/recurring.detector';
import {
  visibleSeries, monthlyNut, staleSeries, isPinned, dismissedEntries,
  recurringCandidates, stateForLedger, allRecurringSeries, CandidateSeries, DismissedEntry,
} from './recurring.calc';
import { formatCents } from '../format';

@Component({
  selector: 'app-money-recurring',
  templateUrl: './recurring.component.html',
  styleUrls: ['./recurring.component.scss'],
  standalone: false,
})
export class RecurringComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  expenses: RecurringSeries[] = [];
  income: RecurringSeries[] = [];
  stale: RecurringSeries[] = [];
  candidates: CandidateSeries[] = [];
  dismissed: DismissedEntry[] = [];
  showDismissed = false;
  nutCents = 0;

  readonly fmt = formatCents;

  private uid = '';
  private state: Record<string, RecurringState> = {};
  private subs: Subscription[] = [];

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.subs.push(this.money.subscribeAll(this.uid).subscribe({
        next: data => {
          const asOf = new Date().toISOString().slice(0, 10);
          const detected = detectRecurring(data.txns, asOf);

          // recurring-state is keyed by hash64(seriesKey) for RTDB safety;
          // stateForLedger re-keys it back to raw series keys (probing every
          // possible billing-day split too) so the pure helpers stay readable.
          this.state = stateForLedger(data.txns, detected, data.recurringState);

          // allRecurringSeries synthesizes a series for every key the user
          // pinned by hand that detection itself never found — that's the
          // entire reason pinning exists — so it flows through the same
          // visibleSeries/staleSeries pipeline as a detected bill. A
          // merchant that is dismissed (whether it was ever pinned first or
          // not) is handled separately below by dismissedEntries, which
          // derives its display figures straight from the transactions — it
          // doesn't need a place in `all`.
          const all = allRecurringSeries(data.txns, detected, this.state, asOf);

          const visible = visibleSeries(all, this.state);
          this.expenses = visible.filter(s => s.direction === 'expense');
          this.income = visible.filter(s => s.direction === 'income');
          this.stale = staleSeries(all, this.state);
          this.nutCents = monthlyNut(all, this.state);
          this.candidates = recurringCandidates(data.txns, detected, this.state);
          this.dismissed = dismissedEntries(detected, data.txns, this.state);
          this.loading = false;
        },
        error: () => { this.error = 'Could not load your ledger.'; this.loading = false; },
      }));
    }));
  }

  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  isConfirmed(s: RecurringSeries): boolean { return this.state[s.key]?.status === 'confirmed'; }
  isPinnedSeries(s: RecurringSeries): boolean { return isPinned(s.key, this.state); }

  async confirm(s: RecurringSeries): Promise<void> {
    try { await this.money.setRecurringState(this.uid, s.key, 'confirmed'); }
    catch { this.error = 'Could not save that.'; }
  }

  async dismiss(s: RecurringSeries): Promise<void> {
    try { await this.money.setRecurringState(this.uid, s.key, 'dismissed'); }
    catch { this.error = 'Could not save that.'; }
  }

  /** "Add" on a Not-detected row: the user has said this is a bill. */
  async pin(key: string): Promise<void> {
    try { await this.money.setRecurringState(this.uid, key, 'pinned'); }
    catch { this.error = 'Could not save that.'; }
  }

  /** Un-pin returns the series to a plain confirmed state rather than deleting the decision. */
  async unpin(s: RecurringSeries): Promise<void> {
    try { await this.money.setRecurringState(this.uid, s.key, 'confirmed'); }
    catch { this.error = 'Could not save that.'; }
  }

  /**
   * Restore from the Dismissed section — dismissal is reversible, not
   * permanent. Always writes 'confirmed', even for an entry that was never
   * detected: it belongs back in "Not detected", not the fixed-cost list.
   */
  async restore(e: DismissedEntry): Promise<void> {
    try { await this.money.setRecurringState(this.uid, e.key, 'confirmed'); }
    catch { this.error = 'Could not save that.'; }
  }

  /** "×" on a Not-detected row: the user has said this is not a bill either. */
  async dismissCandidate(key: string): Promise<void> {
    try { await this.money.setRecurringState(this.uid, key, 'dismissed'); }
    catch { this.error = 'Could not save that.'; }
  }

  toggleDismissed(): void { this.showDismissed = !this.showDismissed; }

  trackSeries(_: number, s: RecurringSeries): string { return s.key; }
  trackCandidate(_: number, c: CandidateSeries): string { return c.key; }
  trackDismissed(_: number, e: DismissedEntry): string { return e.key; }
}
